import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    ConfirmationSummaryModel,
    DateUtilities,
    EventService,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationModel,
    NotificationService,
    OtherTasksService,
    SignaturePad,
    SignaturePadOptions,
    SynchronizationService,
    TimeRecordingEntryModel,
    TimeRecordingGeneralEntryModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, EMPTY, from, of, Subject } from 'rxjs';
import { catchError, finalize, mergeMap, take, takeUntil } from 'rxjs/operators';
import { ConfirmationService } from '../services/confirmation.service';
import { ConfirmationSignatureComponent } from './confirmation-signature/confirmation-signature.component';

@Component({
    selector: 'pace-confirmation-summary',
    templateUrl: './confirmation-summary.component.html',
    styleUrls: ['./confirmation-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationSummaryComponent implements OnInit, AfterViewInit, OnDestroy {
    private destroy$: Subject<void>;
    // bindings
    @Input()
    public model: ConfirmationSummaryModel;

    @ViewChild('customerSignatureCanvasElement')
    public customerSignatureCanvasElement: ElementRef;

    @ViewChild('cseSignatureCanvasElement')
    public cseSignatureCanvasElement: ElementRef;

    public confirmationModel: ConfirmationModel;

    public readonly signatureImageMaxSize = 9000;
    public customerSignaturePad: SignaturePad;
    public cseSignaturePad: SignaturePad;
    public isLoading = false;
    public notification: NotificationModel;

    public displayCustomerSignature = true;
    public displayCseSignature = true;
    public displayMaterial = true;
    public displaySystemRecovery = true;
    public displayTravelTo = true;
    public displayReportCodes = false;
    public displayReportText = false;
    public displayTroubleText = false;
    public displayDistance: boolean | undefined;
    public displayParticipants = false;
    public displayFollowUp = false;
    public isShortConfirmation = false;
    public displayTravelBackSummary: boolean;
    public followUpSelectedEntry: string;
    public displayCustomerContact = false;
    public displayMobileNumber = false;
    public displayMeasurementTool = false;
    public measurementToolsEmptyMessage = '';
    public displayCustomerInformation = false;
    public displayMeasurementToolDetails = false;
    public displayConfirmSignatures = false;

    public readonly optionValueYes = ConfigConstants.optionValueYes;
    public readonly optionValueNo = ConfigConstants.optionValueNo;

    public confirmationSummaryFormChildren: FormArray;
    public confirmationSummaryForm: FormGroup;
    public readonly NOTIFICATION_STATUS_CODE = NOTIFICATION_STATUS_CODE;
    public remoteWorkLabel: string;
    public affectedEquipment: string | undefined;
    public selfAssignedTaskOriginalPlannedDate: moment.Moment | undefined;
    public isRadioActiveToolsUsed: boolean;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly confirmationService: ConfirmationService,
        private readonly appNotificationService: AppNotificationService,
        private readonly notificationService: NotificationService,
        private readonly configService: ConfigService,
        private readonly router: Router,
        private readonly formBuilder: FormBuilder,
        public readonly modalInstance: BsModalRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly synchronizationService: SynchronizationService,
        private readonly otherTasksService: OtherTasksService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly modalService: BsModalService,
        private readonly eventService: EventService,
    ) {
        this.displayTravelTo = !this.clientManager.isNgmP58CHNL();
        this.displayReportCodes = !this.clientManager.isPaceUS();
        this.displayReportText = !this.clientManager.isPaceUS();
        this.displayTroubleText = this.clientManager.isNgmAAJapan();
        this.displaySystemRecovery = !this.clientManager.isPaceUS();
        this.displayFollowUp = !this.clientManager.isPaceUS();
        this.displayMeasurementTool = this.configService.getShowCalibrationDate();
        this.displayCustomerInformation = this.configService.getShowCustomerInformationOnSummary();
        this.displayConfirmSignatures = this.configService.getDisplayConfirmSignatures();
        this.destroy$ = new Subject<void>();
    }

    public ngOnInit(): void {
        this.displayMobileNumber = this.configService.getShowMobileNumberForParticipants();
        this.displayTravelBackSummary = this.configService.getDisplayTravelBackSummary();
        this.confirmationModel = this.model.confirmationModel;
        this.otherTasksService.triggerSyncConcurrent();
        this.otherTasksService
            .getTaskOriginalPlannedDate(this.model.confirmationModel.notificationIdentifier)
            .pipe(
                catchError((error) => {
                    this.log.error(
                        `${this.constructor.name}: error retrieving originally planned date for notification`,
                        this.model.confirmationModel.notificationIdentifier,
                        error,
                    );
                    return EMPTY;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((originalPlannedDate) => {
                this.selfAssignedTaskOriginalPlannedDate = originalPlannedDate;
                this.changeDetectorRef.markForCheck();
            });

        const notification = this.notificationService.getNotificationByIdentifier(
            this.model.confirmationModel.notificationIdentifier,
        );
        if (!notification) {
            this.log.error(
                `${this.constructor.name}: did not find a notification with identifier ${this.model.confirmationModel.notificationIdentifier}`,
            );
            throw new Error(
                `did not find a notification with identifier ${this.model.confirmationModel.notificationIdentifier}`,
            );
        }
        this.notification = notification;

        this.isShortConfirmation = this.confirmationModel.type === CONFIRMATION_TYPES.SHORT;
        this.displayCustomerSignature = !this.isShortConfirmation;
        this.displayCseSignature = !this.isShortConfirmation && !!this.model.cseSignature;
        this.displayCustomerContact = this.configService.getDisplayCustomerContact();
        this.displayMeasurementToolDetails =
            !!this.confirmationModel.measurementTool &&
            this.confirmationModel.measurementTool.showMeasurementToolsDetails;

        if (
            this.clientManager.isPaceUS() ||
            (this.isShortConfirmation && typeof this.confirmationModel.materials === 'undefined') ||
            this.configService.showParticipantList(this.notification.taskGroup)
        ) {
            this.displayMaterial = false;
        }

        this.displayParticipants =
            !this.isShortConfirmation && this.configService.showParticipantList(this.notification.taskGroup);

        const confirmationSummaryValidator = (): ValidationErrors | null => this.checkValidity();
        this.confirmationSummaryFormChildren = this.formBuilder.array([]);
        this.confirmationSummaryForm = this.formBuilder.group(
            {
                children: this.confirmationSummaryFormChildren,
            },
            {
                validators: confirmationSummaryValidator,
            },
        );
        if (this.isShortConfirmation) {
            this.followUpSelectedEntry = ConfigConstants.optionYes;
        } else {
            this.followUpSelectedEntry = this.confirmationModel.followup
                ? this.confirmationModel.followup.followupOptions.selectedEntry.displayName
                : ConfigConstants.optionNA;
        }

        this.measurementToolsEmptyMessage = !this.model.measurementToolsDetailsList?.length
            ? <string>this.translate.instant('CONFIRMATION_SUMMARY_NO_MEASUREMENT_TOOL_MESSAGE')
            : '';
        this.remoteWorkLabel = this.model.confirmationModel.notificationCurrentStatus.isRemoteWorkOrTraining()
            ? <string>this.translate.instant('CONFIRMATION_SUMMARY_REMOTE_WORK_LABEL')
            : <string>this.translate.instant('CONFIRMATION_SUMMARY_REMOTE_TRAINING_LABEL');
        this.displayDistance = this.model.confirmationModel.expenses?.displayDistance;
        if (this.confirmationModel.equipment && this.clientManager.isNgmP58()) {
            this.affectedEquipment = this.confirmationModel.equipment.equipments.selectedEntry
                ? this.confirmationModel.equipment.equipments.selectedEntry.model.equiShortDesc.value
                : this.notification.getMainEquipment()?.equiShortDesc.value;
        }
        this.isRadioActiveToolsUsed = this.model.isRadioActiveToolUsed();
    }

    public ngAfterViewInit(): void {
        const signatureOptions: SignaturePadOptions = {
            outputImageType: 'image/jpeg',
            outputImageMaxSize: this.signatureImageMaxSize,
            backgroundColor: 'rgba(255, 255, 255, 1)',
            onEnd: () => {
                this.confirmationSummaryForm.updateValueAndValidity();
                // we need to add the markForCheck, so that the signatureChanges, and formValidation gets updated.
                // Otherwise, formValidation will not get updated.
                this.changeDetectorRef.markForCheck();
            },
        };

        // short confirmation does not contain any material and does not require a signature
        if (this.displayCustomerSignature) {
            this.customerSignaturePad = new SignaturePad(
                this.log,
                <HTMLCanvasElement>this.customerSignatureCanvasElement.nativeElement,
                signatureOptions,
            );

            this.customerSignaturePad.dataUrl$.pipe(takeUntil(this.destroy$)).subscribe((customerSignature) => {
                this.model.customerSignature.value = customerSignature;
            });
        }
        if (this.displayCseSignature) {
            this.cseSignaturePad = new SignaturePad(
                this.log,
                <HTMLCanvasElement>this.cseSignatureCanvasElement.nativeElement,
                signatureOptions,
            );
            this.cseSignaturePad.dataUrl$.pipe(takeUntil(this.destroy$)).subscribe((cseSignature) => {
                this.model.cseSignature.value = cseSignature;
            });
        }

        // Customer signature is not available in case of Remote work
        if (this.model.confirmationModel.isNotificationRemoteWork) {
            this.model.customerNotAvail.model.value = true;
            this.customerNotAvailChanged();
        }
        // The change detection needs to be delayed to avoid an ExpressionChangedAfterItHasBeenCheckedError
        Promise.resolve(undefined)
            .then(() => {
                this.changeDetectorRef.markForCheck();
            })
            .catch((error: Error) => {
                this.log.error(
                    'Error in ConfirmationSummaryComponent -> ngAfterViewInit -> changeDetectorRef.markForCheck: ',
                    error,
                );
            });
    }

    public clearCustomerSignature(): void {
        this.customerSignaturePad.clear();
        this.confirmationSummaryForm.updateValueAndValidity();
    }

    public clearCseSignature(): void {
        this.cseSignaturePad.clear();
        this.confirmationSummaryForm.updateValueAndValidity();
    }

    public customerNotAvailChanged(): void {
        if (this.model.customerNotAvail.value) {
            this.customerSignaturePad.clear();
            this.customerSignaturePad.off();
        } else {
            this.customerSignaturePad.on();
        }
    }

    /**
     * this function is added as part of the fix for the partial/ missing signature issue
     * ticket: https://evorait.atlassian.net/browse/SHP-5793
     */
    public confirmSignatures(): void {
        if (this.displayConfirmSignatures && (!this.model.customerNotAvail.value || this.displayCseSignature)) {
            combineLatest([this.customerSignaturePad.dataUrl$, this.cseSignaturePad.dataUrl$])
                .pipe(
                    take(1),
                    mergeMap(([customerSignature, cseSignature]) => {
                        const modalInstance = this.modalService.show(ConfirmationSignatureComponent, {
                            backdrop: 'static',
                            class: ModalClass.MODAL_LARGE,
                            initialState: {
                                customerSignature,
                                cseSignature,
                            },
                            ariaLabelledBy: 'confirmation-signature-modal-title',
                        });
                        return modalInstance.content?.modalResult ?? of(false);
                    }),
                )
                .subscribe((modalResult) => {
                    if (modalResult) {
                        this.submit();
                    }
                });
        } else {
            this.submit();
        }
    }

    public submit(): void {
        this.isLoading = true;

        this.confirmationService
            .submitConfirmation(this.confirmationModel)
            .pipe(
                take(1),
                mergeMap(() => {
                    this.modalInstance.hide();
                    this.eventService.triggerRefreshMaterialReturnCount();
                    return from(this.router.navigate([ConfigConstants.routeUrlDashboard]));
                }),
                mergeMap(() => {
                    this.log.debug(`ConfirmationSummaryComponent: submitConfirmation: sendOfflineData`);
                    return this.synchronizationService.sendOfflineData();
                }),
                mergeMap(() => {
                    if (this.selfAssignedTaskOriginalPlannedDate) {
                        return this.otherTasksService
                            .removeTaskOriginalPlannedDate(this.model.confirmationModel.notificationIdentifier)
                            .pipe(
                                catchError((error) => {
                                    this.log.error(
                                        'ConfirmationSummaryComponent: Error while removing confirmation: ',
                                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                                    );
                                    return of(undefined);
                                }),
                            );
                    }
                    return of(undefined);
                }),
                catchError((error) => {
                    this.log.error(
                        'ConfirmationSummaryComponent: Error while submitting confirmation: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CONFIRMATION_SUMMARY_SUBMIT_ERROR_TITLE'),
                        <string>this.translate.instant('CONFIRMATION_SUMMARY_SUBMIT_ERROR_BODY'),
                    );
                    return of(undefined);
                }),
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe();
    }

    private checkValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};
        const customerNotAvail = this.model.customerNotAvail.value;

        if (!customerNotAvail && this.customerSignaturePad && this.customerSignaturePad.isEmpty) {
            errors['customer-signature-missing'] = 'Customer signature is missing';
        }
        if (this.cseSignaturePad && this.cseSignaturePad.isEmpty) {
            errors['cse-signature-missing'] = `CSE signature is missing`;
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public trackById(index: number, entry: TimeRecordingGeneralEntryModel): number {
        return entry.id;
    }

    public formatDateTime(dateTime: moment.Moment): string {
        return dateTime.isValid() ? `${DateUtilities.formatDate(dateTime)} ${DateUtilities.formatTime(dateTime)}` : '';
    }

    public formatDuration(duration: moment.Duration): string {
        let formattedDuration = '';
        if (duration) {
            formattedDuration = `${duration.asMinutes()} ${<string>(
                this.translate.instant('CONFIRMATION_EVENT_SUMMARY_MINUTES_LABEL')
            )}`;
            if (this.clientManager.isNgmAP0()) {
                const fixedHoursLength = 2;
                formattedDuration = duration.asHours().toFixed(fixedHoursLength) + ' hours';
            }
        }
        return formattedDuration;
    }

    public formatEntry(entry: TimeRecordingEntryModel, endEntry?: TimeRecordingEntryModel): string {
        let formattedDuration = '';
        if (entry) {
            let formattedTimeStart;
            let formattedTimeEnd;

            // first condition checks USE CASE 1, second condition checks USE CASE 2 and 3.
            if (
                (ConfirmationSummaryComponent.isMultiDayEntry(entry) && !endEntry) ||
                ConfirmationSummaryComponent.isMultiDayEntry(entry, endEntry)
            ) {
                // multi day entries should generally look like this: 31.07. 23:00 - 01.08. 02:30
                // USE CASE 1: (entry is multi day, no endEntry) =>
                //              entry: 31.07. 23:00 - 01.08. 02:30
                //              result: 31.07. 23:00 - 01.08. 02:30
                // USE CASE 2: (entry is multi day, endEntry is single day on next day) =>
                //              entry: 31.07. 23:00 - 01.08. 02:30 | endEntry 01.08. 04:00 - 04:45
                //              result: 31.07. 23:00 - 01.08. 04:45
                // USE CASE 3: (entry is single day, but endEntry is next day) =>
                //              entry: 31.07. 23:00 - 31.07. 23:30 | endEntry 31.07. 23:45 - 01.08. 02:45
                //              result: 31.07. 23:00 - 01.08. 02:45
                formattedTimeStart = `${DateUtilities.formatDate(entry.startDateTime)} ${DateUtilities.formatTime(entry.startDateTime)}`;
                formattedTimeEnd = `${DateUtilities.formatDate(entry.endDateTime)} ${DateUtilities.formatTime(entry.endDateTime)}`;
                if (endEntry) {
                    formattedTimeEnd = `${DateUtilities.formatDate(endEntry.endDateTime)} ${DateUtilities.formatTime(endEntry.endDateTime)}`;
                }
            } else {
                // single day entries should look like this: 31.07. 10:00 - 10:30
                formattedTimeStart = `${DateUtilities.formatDate(entry.startDateTime)} ${DateUtilities.formatTime(entry.startDateTime)}`;
                formattedTimeEnd = DateUtilities.formatTime(entry.endDateTime);
                if (endEntry) {
                    formattedTimeEnd = DateUtilities.formatTime(endEntry.endDateTime);
                }
            }

            // put all together
            formattedDuration = formattedTimeStart + ' - ' + formattedTimeEnd;
        }
        return formattedDuration;
    }

    /**
     * Check if either a single entry has its start and end time on the same day or if a second parameter is passed, if
     * the start of the first entry and the end of the other entry is on the same day.
     *
     * @param entry the entry to check
     * @param endEntry the end entry to check
     * @returns true if the entry is a multi day entry
     */
    public static isMultiDayEntry(entry: TimeRecordingEntryModel, endEntry?: TimeRecordingEntryModel): boolean {
        if (!endEntry) {
            return entry.endDateTime.clone().startOf('day').diff(entry.startDateTime.clone().startOf('day')) > 0;
        } else {
            return endEntry.endDateTime.clone().startOf('day').diff(entry.startDateTime.clone().startOf('day')) > 0;
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }
}
